import type { CCPreset } from '~/types/presets';
import type { MidiCC } from '~/types/midi';
import { getYoTestSynthDefaultProfile } from '~/lib/profiles/yoTestSynthProfile';

/**
 * "Init" CC preset for YO! Monosynth
 *
 * This preset uses warm, rounded settings optimized for playing alongside FM Synth
 * without causing phase interference or harsh buzzing.
 *
 * Key settings (optimized for multi-synth playback):
 * - oscillator: triangle (softer harmonics - avoids clashing with FM)
 * - envelope: attack 0.03s, decay 0.35s, sustain 0.35, release 0.9s
 * - filterEnvelope: attack 0.01s, decay 0.5s, sustain 0.15, release 0.9s, baseFrequency 400Hz, octaves 3
 * - detune: -5 cents (opposite direction from FM for maximum phase separation)
 */

const initMonosynthCCs: MidiCC[] = [
  { number: 1, value: 0, scramble: false, smooth: false, show: true, min: 0, max: 127 },
  { number: 7, value: 100, scramble: false, smooth: false, show: true, min: 80, max: 110 },
  { number: 11, value: 100, scramble: false, smooth: false, show: true, min: 80, max: 110 },
  // Filter Envelope (warmer, smoother settings)
  { number: 16, value: 8, scramble: false, smooth: false, show: true, min: 0, max: 127 },   // Filter Env Attack: 0.01s (slightly softer)
  { number: 17, value: 63, scramble: false, smooth: false, show: true, min: 0, max: 127 },  // Filter Env Decay: 0.5s (smoother)
  { number: 18, value: 19, scramble: false, smooth: false, show: true, min: 0, max: 127 },  // Filter Env Sustain: 0.15 (slightly more body)
  { number: 19, value: 57, scramble: false, smooth: false, show: true, min: 0, max: 127 },  // Filter Env Release: 0.9s (longer tail)
  { number: 20, value: 54, scramble: true, smooth: false, show: true, min: 0, max: 60 },    // Filter Env Octaves: 3 (less extreme) ✅ SCRAMBLED
  { number: 21, value: 54, scramble: false, smooth: false, show: true, min: 20, max: 110 }, // Filter Env Base Freq: 400Hz (warmer)
  // Amp Envelope (softer attack, rounder sound)
  { number: 70, value: 44, scramble: true, smooth: false, show: true, min: 0, max: 127 },   // Amp Sustain: 0.35 ✅ SCRAMBLED (volume shape)
  { number: 71, value: 5, scramble: false, smooth: false, show: true, min: 0, max: 50 },    // Filter Resonance: very low for warmth
  { number: 72, value: 57, scramble: true, smooth: false, show: true, min: 10, max: 127 },  // Amp Release: 0.9s ✅ SCRAMBLED (tail length)
  { number: 73, value: 15, scramble: false, smooth: false, show: true, min: 3, max: 80 },   // Amp Attack: 0.03s (softer onset)
  { number: 74, value: 33, scramble: true, smooth: false, show: true, min: 40, max: 90 },   // Filter Cutoff: warm ✅ SCRAMBLED (brightness)
  { number: 75, value: 59, scramble: true, smooth: false, show: true, min: 10, max: 127 },  // Amp Decay: 0.35s ✅ SCRAMBLED (punch)
  { number: 76, value: 112, scramble: false, smooth: false, show: true, min: 0, max: 127 }, // Oscillator Type: triangle (softer harmonics)
  { number: 77, value: 64, scramble: false, smooth: false, show: true, min: 64, max: 64 },  // Detune: 0 cents (no beating)
];

/**
 * "Init" Monosynth CC Preset
 */
export const INIT_MONOSYNTH_PRESET: CCPreset = {
  id: 'init-mono',
  name: 'Init',
  deviceType: 'MonoSynth',
  cc: initMonosynthCCs,
  profiles: [getYoTestSynthDefaultProfile()],
  activeProfileId: 'yo-test-synth-default',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  userId: null, // System preset
  isPublic: true,
};

/**
 * Get the "Init" Monosynth CC preset
 * Returns a deep copy to avoid mutation
 */
export function getInitMonosynthPreset(): CCPreset {
  return {
    ...INIT_MONOSYNTH_PRESET,
    cc: INIT_MONOSYNTH_PRESET.cc.map(cc => ({ ...cc })),
    profiles: INIT_MONOSYNTH_PRESET.profiles.map(profile => ({
      ...profile,
      ccConfig: profile.ccConfig.map(config => ({ ...config })),
    })),
  };
}
